import React, { useState, useEffect } from 'react';
import { emailAPI } from '../../services/api';
import './emailHistory.css';

const EmailHistory = ({ projectId }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  // Per-row thread state. Keyed by EmailLog.Id.
  //   undefined = thread not loaded yet
  //   { loading: true } = fetching
  //   { error: string } = fetch failed (or thread tracking not available)
  //   { messages: [...] } = loaded
  const [threads, setThreads] = useState({});
  const [openIds, setOpenIds] = useState(new Set());
  // Attachments currently downloading, keyed by `${logId}:${attachmentId}`,
  // so we can show a spinner and prevent double-clicks.
  const [downloadingAtt, setDownloadingAtt] = useState(new Set());

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const res = await emailAPI.getHistory(projectId);
      setEmails(res.data);
    } catch (err) {
      console.error('Error loading email history:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleThread = async (email) => {
    const next = new Set(openIds);
    if (next.has(email.id)) {
      next.delete(email.id);
      setOpenIds(next);
      return;
    }
    next.add(email.id);
    setOpenIds(next);

    // Fetch thread on first open if not cached.
    if (!threads[email.id]) {
      setThreads((t) => ({ ...t, [email.id]: { loading: true } }));
      try {
        const res = await emailAPI.getThread(email.id);
        if (res.data?.success) {
          setThreads((t) => ({ ...t, [email.id]: { messages: res.data.messages || [] } }));
        } else {
          setThreads((t) => ({
            ...t,
            [email.id]: { error: res.data?.message || 'Could not load thread.' },
          }));
        }
      } catch (err) {
        setThreads((t) => ({
          ...t,
          [email.id]: { error: err.response?.data?.message || 'Failed to fetch thread.' },
        }));
      }
    }
  };

  const downloadAttachment = async (logId, att) => {
    if (!att.attachmentId || !att.messageId) return;
    const key = `${logId}:${att.attachmentId}`;
    if (downloadingAtt.has(key)) return;

    setDownloadingAtt((s) => new Set(s).add(key));
    try {
      const res = await emailAPI.downloadAttachment(logId, {
        messageId: att.messageId,
        attachmentId: att.attachmentId,
        filename: att.filename,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = att.filename || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      alert('Could not download this attachment. Please try again.');
    } finally {
      setDownloadingAtt((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  };

  const formatSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const iconForAttachment = (mimeType, filename) => {
    const mt = (mimeType || '').toLowerCase();
    const name = (filename || '').toLowerCase();
    if (mt.startsWith('image/')) return 'fa-file-image';
    if (mt === 'application/pdf' || name.endsWith('.pdf')) return 'fa-file-pdf';
    if (mt.includes('word') || /\.docx?$/.test(name)) return 'fa-file-word';
    if (mt.includes('sheet') || mt.includes('excel') || /\.xlsx?$|\.csv$/.test(name)) return 'fa-file-excel';
    if (mt.includes('zip') || /\.(zip|rar|7z)$/.test(name)) return 'fa-file-archive';
    return 'fa-file';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="email-history-loading">Loading email history...</div>;
  }

  if (emails.length === 0) {
    return (
      <div className="email-history-empty">
        <i className="fas fa-inbox"></i>
        <p>No emails sent for this project yet.</p>
      </div>
    );
  }

  return (
    <div className="email-history">
      <div className="email-history-header">
        <h3><i className="fas fa-history"></i> Email History</h3>
        <span className="email-count">{emails.length} email{emails.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="email-history-list">
        {emails.map((email) => {
          const isOpen = openIds.has(email.id);
          const thread = threads[email.id];
          const canFetchThread = !!email.gmailThreadId;
          return (
            <div key={email.id} className="email-history-item">
              <div
                className={`email-item-clickable${canFetchThread ? '' : ' email-item-no-thread'}`}
                onClick={() => canFetchThread && toggleThread(email)}
                role={canFetchThread ? 'button' : undefined}
                tabIndex={canFetchThread ? 0 : undefined}
                onKeyDown={(e) => {
                  if (canFetchThread && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    toggleThread(email);
                  }
                }}
                title={canFetchThread ? 'Click to view thread' : 'Thread tracking unavailable for this email'}
              >
                <div className="email-item-top">
                  <div className="email-item-to">
                    <i className={`fas fa-${canFetchThread ? (isOpen ? 'chevron-down' : 'chevron-right') : 'envelope'}`}></i>
                    <span>{email.toEmail}</span>
                  </div>
                  <span className={`email-status-badge ${email.status.toLowerCase()}`}>
                    {email.status}
                  </span>
                </div>
                <div className="email-item-subject">{email.subject}</div>
                <div className="email-item-meta">
                  <span><i className="fas fa-user"></i> {email.sentBy}</span>
                  {email.templateName && (
                    <span><i className="fas fa-file-alt"></i> {email.templateName}</span>
                  )}
                  <span><i className="fas fa-clock"></i> {formatDate(email.sentAt)}</span>
                </div>
              </div>

              {isOpen && (
                <div className="email-thread-panel">
                  {thread?.loading && <div className="email-thread-loading">Loading thread…</div>}
                  {thread?.error && <div className="email-thread-error">{thread.error}</div>}
                  {thread?.messages && thread.messages.length === 0 && (
                    <div className="email-thread-empty">No messages in this thread.</div>
                  )}
                  {thread?.messages && thread.messages.length > 0 && (
                    <div className="email-thread-messages">
                      {thread.messages.map((m) => (
                        <div
                          key={m.id}
                          className={`email-thread-msg ${m.isFromUs ? 'from-us' : 'from-them'}`}
                        >
                          <div className="email-thread-msg-header">
                            <div className="email-thread-msg-from">
                              <strong>{m.from || '(unknown sender)'}</strong>
                            </div>
                            <div className="email-thread-msg-date">{formatDate(m.date)}</div>
                          </div>
                          {m.to && (
                            <div className="email-thread-msg-to">To: {m.to}</div>
                          )}
                          {m.subject && (
                            <div className="email-thread-msg-subject">{m.subject}</div>
                          )}
                          {/* Prefer HTML body if available; fall back to plain text. */}
                          {m.bodyHtml ? (
                            <div
                              className="email-thread-msg-body html"
                              // The bodies come from Gmail's API for accounts the
                              // user has OAuth'd into — treating them as trusted.
                              dangerouslySetInnerHTML={{ __html: m.bodyHtml }}
                            />
                          ) : m.bodyText ? (
                            <pre className="email-thread-msg-body plain">{m.bodyText}</pre>
                          ) : m.snippet ? (
                            <div className="email-thread-msg-body snippet">{m.snippet}…</div>
                          ) : null}
                          {m.attachments && m.attachments.length > 0 && (
                            <div className="email-thread-attachments">
                              <span className="email-thread-attachments-label">
                                <i className="fas fa-paperclip"></i>
                                {m.attachments.length} attachment{m.attachments.length !== 1 ? 's' : ''}
                              </span>
                              {m.attachments.map((a, i) => {
                                const canDownload = !!a.attachmentId && !!a.messageId;
                                const isDownloading = downloadingAtt.has(`${email.id}:${a.attachmentId}`);
                                return (
                                  <button
                                    type="button"
                                    className={`email-attachment-chip${canDownload ? ' downloadable' : ''}`}
                                    key={`${a.filename}-${i}`}
                                    title={canDownload ? `Download ${a.filename}` : a.filename}
                                    onClick={() => canDownload && downloadAttachment(email.id, a)}
                                    disabled={!canDownload || isDownloading}
                                  >
                                    <i className={`fas ${isDownloading ? 'fa-spinner fa-spin' : iconForAttachment(a.mimeType, a.filename)}`}></i>
                                    <span className="email-attachment-name">{a.filename}</span>
                                    {a.sizeBytes != null && (
                                      <span className="email-attachment-size">{formatSize(a.sizeBytes)}</span>
                                    )}
                                    {canDownload && !isDownloading && (
                                      <i className="fas fa-download email-attachment-dl"></i>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmailHistory;
